import React, { useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Icon from "../../../components/AppIcon";
import { roundTo } from "../../../components/utils/mathUtils";
import { formatQTZ } from "../../../components/utils/format";
import {
  calcularPanelesNecesarios,
  calcularSistemaDesdePanel,
} from "../../../components/utils/infoPanel";
import { logEvent, TRACKING_EVENTS } from "../../../lib/analytics";
import { useConfig } from "../../../context/ConfigContext";

const CalculatorPreview = () => {
  const config = useConfig();
  const {
    PHONE_NUMBER_FORM,
    WHATSAPP_NUMBER_FORM,
    CALL_PHONE_NUMBER,
    WHATSAPP_NUMBER_CHAT,
    guatemalaRegions,
  } = config;

  const [formData, setFormData] = useState({
    monthlyBill: "",
    region: "",
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handleInputChange = (field, value) => {
    setValidationError("");
    setResults(null);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateSavings = () => {
    if (!formData?.monthlyBill || !formData?.region) {
      return;
    }

    logEvent("Calculadora", "enviar_calculadora_previa", `Factura: Q${formData.monthlyBill} - Región: ${formData.region}`, parseFloat(formData.monthlyBill));

    const monthlyBill = parseFloat(formData?.monthlyBill);
    // PASO 1: Calcular paneles necesarios
    const resultPaneles = calcularPanelesNecesarios(monthlyBill, config);

    if (resultPaneles.error) {
      setValidationError(resultPaneles.message);
      return;
    }

    setIsCalculating(true);

    // Simulate calculation delay
    setTimeout(() => {
      // PASO 2: Calcular sistema completo desde paneles
      const sistemaCompleto = calcularSistemaDesdePanel(
        resultPaneles.panelesNecesarios,
        formData.region,
        monthlyBill,
        config
      );

      // PASO 3: Establecer resultados
      setResults(sistemaCompleto);
      setIsCalculating(false);
    }, 500);
  };

  const resetCalculator = () => {
    setFormData({
      monthlyBill: "",
      region: "",
    });
    setResults(null);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Calculator" size={16} />
            <span>Calculadora Solar</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Descubre Tus Ahorros Solares
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Obtén una estimación personalizada de cuánto puedes ahorrar con
            energía solar en tu hogar
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl solar-shadow-elevated ">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Calculator Form */}
              <div className="p-8 lg:p-12">
                <h3 className="text-xl font-semibold text-text-primary mb-6">
                  Información Básica
                </h3>

                <div className="space-y-6">
                  <Input
                    label="Factura Eléctrica Mensual kWh"
                    type="number"
                    placeholder="Ej: 300"
                    value={formData?.monthlyBill}
                    onChange={(e) =>
                      handleInputChange("monthlyBill", e?.target?.value)
                    }
                    description="Ingresa el monto promedio en kWh"
                    required
                  />

                  <Select
                    label="Región"
                    options={guatemalaRegions?.map((item) => ({
                      label: item.label,
                      value: item.id,
                    }))}
                    value={formData?.region}
                    onChange={(value) => handleInputChange("region", value)}
                    placeholder="Selecciona tu región"
                    required
                  />
                </div>

                <div className="flex gap-4 mt-8">
                  <Button
                    variant="default"
                    onClick={calculateSavings}
                    loading={isCalculating}
                    disabled={!formData?.monthlyBill || !formData?.region}
                    iconName="Calculator"
                    iconPosition="left"
                    className="bg-cta-orange hover:bg-cta-orange/90 flex-1"
                  >
                    {isCalculating ? "Calculando..." : "Calcular Ahorros"}
                  </Button>

                  {results && (
                    <Button
                      variant="outline"
                      onClick={resetCalculator}
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Nuevo Cálculo
                    </Button>
                  )}
                </div>
                {validationError && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      className="text-red-500"
                    />
                    <span className="text-sm text-red-700">
                      {validationError}
                    </span>
                  </div>
                )}
              </div>

              {/* Results Panel */}
              <div className="bg-gradient-to-br from-muted/30 to-primary/5 p-8 lg:p-12">
                {!results && !isCalculating && (
                  <div className="text-center h-full flex items-center justify-center">
                    <div>
                      <Icon
                        name="Calculator"
                        size={64}
                        className="mx-auto mb-4 text-muted-foreground opacity-50"
                      />
                      <h4 className="text-lg font-medium text-text-primary mb-2">
                        Resultados Instantáneos
                      </h4>
                      <p className="text-text-secondary">
                        Completa la información para ver tu potencial de ahorro
                      </p>
                    </div>
                  </div>
                )}

                {isCalculating && (
                  <div className="text-center h-full flex items-center justify-center">
                    <div>
                      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <h4 className="text-lg font-medium text-text-primary mb-2">
                        Analizando tu Potencial Solar
                      </h4>
                      <p className="text-text-secondary">
                        Calculando ahorros personalizados...
                      </p>
                    </div>
                  </div>
                )}

                {!isCalculating && results && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-semibold text-text-primary mb-2">
                        Tu Potencial de Ahorro
                      </h4>
                      <p className="text-text-secondary">
                        Estimación basada en tu información
                      </p>
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 solar-shadow-card text-center">
                        <div className="text-2xl font-bold text-success mb-1">
                          {formatQTZ(results?.monthlySavings)}
                        </div>
                        <div className="text-sm text-text-secondary">
                          Ahorro Mensual
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 solar-shadow-card text-center">
                        <div className="text-2xl font-bold text-primary mb-1">
                          {formatQTZ(results?.yearlySavings)}
                        </div>
                        <div className="text-sm text-text-secondary">
                          Ahorro Anual
                        </div>
                      </div>
                    </div>

                    {/* System Details */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-white rounded-lg solar-shadow-card">
                        <span className="text-text-secondary">
                          Cobertura Energética:
                        </span>
                        <span className="font-semibold text-primary">
                          {roundTo(results?.energiaProducida)} kW
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-white rounded-lg solar-shadow-card">
                        <span className="text-text-secondary">
                          Nueva Factura:
                        </span>
                        <span className="font-semibold text-success">
                          {formatQTZ(results?.monthlyBillAfter)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-white rounded-lg solar-shadow-card">
                        <span className="text-text-secondary">
                          CO₂ Reducido/Año:
                        </span>
                        <span className="font-semibold text-secondary">
                          {results?.co2Reduction} kg
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-white rounded-lg solar-shadow-card">
                        <span className="text-text-secondary">
                          Recuperación:
                        </span>
                        <span className="font-semibold text-accent">
                          {roundTo(results?.paybackPeriod)} años
                        </span>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t border-border">
                      <Link to="/calculadora">
                        <Button
                          variant="default"
                          fullWidth
                          iconName="ArrowRight"
                          iconPosition="right"
                          className="bg-primary hover:bg-primary/90"
                        >
                          Ver Análisis Completo
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-text-secondary mb-4">
              ¿Necesitas una cotización más detallada?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                iconName="Phone"
                iconPosition="left"
                href={`tel:${CALL_PHONE_NUMBER}`}
              >
                Llamar: {PHONE_NUMBER_FORM}
              </Button>
              <Button
                variant="outline"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() =>
                  window.open(
                    `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER_CHAT}&text=${encodeURIComponent(
                      `¡Hola! 👋 
Estoy interesado en una cotización solar para reducir mi factura eléctrica.
                
            ${formData?.monthlyBill
                        ? `⚡ Actualmente: ${formData.monthlyBill} kWh mensuales`
                        : ""
                      }
            
            ¿Podrían enviarme una propuesta personalizada?
            
            ¡Gracias!`
                    )}`,
                    "_blank"
                  )
                }
              >
                WhatsApp: {WHATSAPP_NUMBER_FORM}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorPreview;
